import React from 'react';
import { View, Image, Pressable, StyleSheet } from 'react-native'
import { Feather } from '@expo/vector-icons';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import * as Auth from 'aws-amplify/auth';
import { useRoute } from '@react-navigation/native';

import { remove } from '../util/storage';
import { AUTH_KEYS } from '../util/storage/keys';
import { useResponsiveStyles } from '../hooks';
import CustomText from '../components/CustomText';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const Header = (props) => {
  const { t } = useTranslation('common');

  const navigation = useNavigation();
  const route = useRoute();
  const { isWeb, screenWidth } = useResponsiveStyles();
  const insets = useSafeAreaInsets();

  // Adjust header elements for viewing the web portal on smaller devices
  const scale = screenWidth < 800 ? 0.75 : 1;

  const getLogo = () => {
    const orgs = {
      'demo': "Demo Proactive Safety Initiative",
      'cats': "MEOW",
      'MagLab': "MagLab Proactive Safety Initiative",
      'WCOE-Deans': "WCOE Proactive Safety Initiative",
      'IMSE': "WCOE Proactive Safety Initiative"
    }
    return orgs[props.organization];
  };

  // V3 color scheme
  const defaultColor = AppStyles.colorSet.textSecondary;
  const activeColor = AppStyles.colorSet.brandPrimary;
  // Use darker text for active labels (gold text has poor contrast on white bg)
  const activeLabelColor = AppStyles.colorSet.textPrimary;

  const accentBarHeight = 4;

  return (
    <View style={styles.container}>
      {/* Dark status bar background so icons are visible */}
      <View style={[styles.statusBarBg, { height: insets.top }]} />
      {/* Gold accent bar */}
      <View style={[styles.accentBar, { height: accentBarHeight }]} />

      <View style={styles.headerContent}>
        {/* Left side - Back/Logout button */}
        <View style={styles.leftSection}>
          {props && (props.type === 'pop' || !props.noLogin) && !props.noApp && (
            <Pressable
              style={({ pressed, hovered, focused }) => [
                styles.button,
                (hovered || focused) && styles.buttonHovered,
                pressed && { opacity: 0.7 },
              ]}
              onPress={async () => {
                if (props.type === 'pop') {
                  // Check if we can go back in the navigation stack
                  if (navigation.canGoBack()) {
                    navigation.pop();
                  } else {
                    // No previous screen - navigate to admin home instead
                    if (props.admin) {
                      navigation.navigate('AdminFilterHome', {
                        organization: props.organization,
                        admin: props.admin,
                        filterOrg: props.organization,
                        userAdmin: props.userAdmin,
                      });
                    } else {
                      // Fallback for non-admin users
                      navigation.navigate('Home', {
                        organization: props.organization,
                        admin: false,
                      });
                    }
                  }
                } else {
                  await remove(AUTH_KEYS.IS_ADMIN);
                  await remove(AUTH_KEYS.IS_USER_ADMIN);
                  Auth.signOut();
                  navigation.navigate('Login', { skipAutoLogin: true });
                }
              }}
              accessibilityRole="button"
              accessibilityLabel={props.type === 'pop' ? t('nav.goBack') : t('nav.logOut')}
              accessibilityHint={props.type === 'pop' ? t('nav.goBackHint') : t('nav.logOutHint')}
            >
              {({ pressed, hovered, focused }) => (
                <View style={styles.buttonContent}>
                  <Feather
                    name={props.type === 'pop' ? 'arrow-left' : 'log-out'}
                    size={24 * scale}
                    color={pressed || hovered || focused ? activeColor : defaultColor}
                  />
                  <CustomText
                    size={11}
                    font="medium"
                    style={{ color: pressed || hovered || focused ? activeLabelColor : defaultColor, marginTop: 2 }}
                  >
                    {props.type === 'pop' ? t('actions.back') : t('nav.logOutButton')}
                  </CustomText>
                </View>
              )}
            </Pressable>
          )}
        </View>

        {/* Center - Logo (tappable to navigate to login/landing) */}
        {!props || (props && !props.noLogin && (
          <Pressable
            onPress={() => navigation.navigate('Login')}
            style={({ hovered, focused, pressed }) => [
              { cursor: 'pointer' },
              (hovered || focused) && { opacity: 0.85 },
              pressed && { opacity: 0.7 },
            ]}
          >
            <Image
              source={require('../assets/images/logo-black.png')}
              style={[styles.logo, { width: 180 * scale, height: 50 * scale }]}
            />
          </Pressable>
        ))}
        {props && props.noLogin && (
          <View style={styles.noLoginLogo}>
            <CustomText size={screenWidth >= 800 ? 24 : 20} font="semiBold" color={AppStyles.colorSet.textPrimary} style={{ textAlign: 'center' }}>
              {getLogo()}
            </CustomText>
            <CustomText size={12} color={AppStyles.colorSet.textMuted} style={{ textAlign: 'center' }}>
              powered by Moneta
            </CustomText>
          </View>
        )}

        {/* Right side - Profile & Help buttons */}
        <View style={styles.rightSection}>
          {props && !props.noLogin && !props.noApp && props.hasOwnProperty('organization') && props.organization && (
            <Pressable
              style={({ hovered, focused }) => [
                styles.button,
                (hovered || focused) && styles.buttonHovered,
              ]}
              onPress={() => navigation.navigate('Settings', props)}
              accessibilityRole="button"
              accessibilityLabel={t('nav.profileLabel')}
              accessibilityHint={t('nav.profileHint')}
            >
              {({ hovered, focused }) => (
                <View style={styles.buttonContent}>
                  <Feather
                    name="user"
                    size={24 * scale}
                    color={route.name === 'Settings' || hovered || focused ? activeColor : defaultColor}
                  />
                  <CustomText
                    size={11}
                    font="medium"
                    style={{ color: route.name === 'Settings' || hovered || focused ? activeLabelColor : defaultColor, marginTop: 2 }}
                  >
                    {t('nav.profile')}
                  </CustomText>
                </View>
              )}
            </Pressable>
          )}
          {props && !props.noApp && props.hasOwnProperty('organization') && props.organization && (
            <Pressable
              style={({ hovered, focused }) => [
                styles.button,
                (hovered || focused) && styles.buttonHovered,
              ]}
              onPress={() => navigation.navigate('SubmitBug', props)}
              accessibilityRole="button"
              accessibilityLabel={t('nav.helpLabel')}
              accessibilityHint={t('nav.helpHint')}
            >
              {({ hovered, focused }) => (
                <View style={styles.buttonContent}>
                  <Feather
                    name="help-circle"
                    size={24 * scale}
                    color={route.name === 'SubmitBug' || hovered || focused ? activeColor : defaultColor}
                  />
                  <CustomText
                    size={11}
                    font="medium"
                    style={{ color: route.name === 'SubmitBug' || hovered || focused ? activeLabelColor : defaultColor, marginTop: 2 }}
                  >
                    {t('nav.help')}
                  </CustomText>
                </View>
              )}
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  statusBarBg: {
    backgroundColor: '#000',
  },
  accentBar: {
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: wp(4),
    paddingVertical: hp(1.5),
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
  },
  leftSection: {
    flex: 1,
    alignItems: 'flex-start',
  },
  rightSection: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    paddingLeft: 16,
  },
  button: {
    cursor: 'pointer',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    // WCAG minimum touch target: 44x44px
    minHeight: 44,
    minWidth: 44,
    justifyContent: 'center',
    borderRadius: 8,
  },
  // Web hover/focus feedback for header nav buttons
  buttonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  buttonContent: {
    alignItems: 'center',
  },
  logo: {
    resizeMode: 'contain',
  },
  noLoginLogo: {
    flex: 2,
    alignItems: 'center',
  },
});

export default Header;